/**
 * Helpers de Facturación
 * 
 * Funciones reutilizables para la lógica de facturación electrónica,
 * derivación de códigos DGII y construcción de payloads.
 */
import { roundTo2 } from './monetario';

// Fecha mínima permitida para fecha_emision: hoy - 1.5 meses (1 mes + 15 días).
// Debe coincidir con parsearFechaEmision/calcularFechaMinimaEmision en el backend
// (core/servidor/controladores/procesos/c_factura.js).
export function calcularFechaMinimaEmisionISO() {
  const hoy = new Date();
  const minima = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  minima.setMonth(minima.getMonth() - 1);
  minima.setDate(minima.getDate() - 15);
  const y = minima.getFullYear();
  const m = String(minima.getMonth() + 1).padStart(2, '0');
  const d = String(minima.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Mapeo de métodos de pago a códigos DGII
export const MAPEO_FORMA_PAGO_DGII = {
  'efectivo': '01',
  'cheque': '02',
  'transferencia': '02',
  'deposito': '02',
  'tarjeta': '03',
  'credito': '04',
  'debito': '03'
};

// Mapeo de códigos de forma de pago del frontend a id_metodo_pago del sistema
// DB metodo_pago: 1=Efectivo, 2=Tarjeta de Crédito, 3=Tarjeta de Débito, 4=Transferencia Bancaria, 5=Cheque
const MAPEO_ID_METODO_PAGO = {
  '01': 1, // Efectivo → DB id 1
  '02': 4, // Transferencia/Depósito → DB id 4 (Transferencia Bancaria)
  '03': 2, // Tarjeta Crédito/Débito → DB id 2 (Tarjeta de Crédito, generic)
  '04': 5  // Cheque → DB id 5
};

// Mapeo de código UI a código DGII real para forma_pago
// Frontend '04' (Cheque) debe enviarse como DGII '02' (Cheques/Transferencia/Depósito)
const MAPEO_UI_A_DGII_FORMA_PAGO = {
  '01': '01', // Efectivo → DGII Efectivo
  '02': '02', // Transferencia → DGII Cheques/Transferencia/Depósito
  '03': '03', // Tarjeta → DGII Tarjeta Crédito/Débito
  '04': '02'  // Cheque → DGII Cheques/Transferencia/Depósito (NOT '04' which is Venta a Crédito)
};

/**
 * Deriva el código de forma de pago DGII desde los pagos programados
 * @param {Array} pagosProgramados - Lista de pagos con id_metodo_pago
 * @param {Array} metodosPago - Catálogo de métodos de pago del sistema
 * @returns {string} Código DGII (01-08)
 */
export function derivarFormaPagoDGII(pagosProgramados, metodosPago) {
  if (!pagosProgramados || pagosProgramados.length === 0) {
    return '04';  // Sin pagos = Venta a Crédito
  }
  
  // Si hay múltiples métodos diferentes = Mixto (08)
  const metodosUsados = new Set(pagosProgramados.map(p => p.id_metodo_pago));
  if (metodosUsados.size > 1) return '08';
  
  // Buscar descripción del método y mapear
  const idMetodo = pagosProgramados[0].id_metodo_pago;
  const metodo = metodosPago?.find(m => m.id_metodo_pago === idMetodo);
  if (!metodo) return '08';
  
  const desc = metodo.descripcion.toLowerCase();
  for (const [key, value] of Object.entries(MAPEO_FORMA_PAGO_DGII)) {
    if (desc.includes(key)) return value;
  }
  return '08';  // Otras formas
}

/**
 * Deriva el tipo de pago DGII (1=Contado, 2=Crédito)
 * @param {Object} formData - Datos del formulario con monto_total y fecha_vencimiento
 * @param {Array} pagosProgramados - Lista de pagos programados
 * @param {string} condicionPago - 'contado' o 'credito'
 * @returns {number} 1 para Contado, 2 para Crédito
 */
export function derivarTipoPagoDGII(formData, pagosProgramados, condicionPago) {
  // Si la condición explícita es crédito = 2
  if (condicionPago === 'credito') return 2;
  
  // Si la condición es contado = 1
  if (condicionPago === 'contado') return 1;
  
  // Fallback: Si tiene fecha_vencimiento futura = Crédito
  if (formData.fecha_vencimiento) {
    const hoy = new Date();
    const vencimiento = new Date(formData.fecha_vencimiento);
    if (vencimiento > hoy) return 2;  // Crédito
  }
  
  // Si no hay pagos programados = Crédito
  if (!pagosProgramados || pagosProgramados.length === 0) return 2;
  
  // Si los pagos cubren el total = Contado
  const totalPagos = pagosProgramados.reduce((s, p) => s + parseFloat(p.monto || 0), 0);
  if (totalPagos >= (formData.monto_total || 0)) return 1;  // Contado
  
  return 2;  // Crédito
}

/**
 * Mapea forma de pago DGII a id_metodo_pago del sistema
 * @param {string} formaPagoDGII - Código DGII ('01', '02', etc.)
 * @returns {number} ID del método de pago en el sistema
 */
export function obtenerIdMetodoPago(codigoFormaPago) {
  return MAPEO_ID_METODO_PAGO[codigoFormaPago] || 1;
}

/**
 * Mapea código UI de forma de pago a código DGII real
 * @param {string} codigoUI - Código UI ('01', '02', '03', '04')
 * @returns {string} Código DGII real
 */
export function obtenerCodigoDGII(codigoUI) {
  return MAPEO_UI_A_DGII_FORMA_PAGO[codigoUI] || codigoUI;
}

/**
 * Mapea una cotización completa (GET /cotizacion/:id) a los campos de ticket
 * usados por ProcesoFacturaDemo/ProcesoPOS para precargar la pantalla de creación
 * de factura al convertir. No crea nada — solo arma el estado del ticket.
 * @param {Object} cotizacionCompleta - Respuesta de GET /cotizacion/:id
 * @param {Object} helpers - { formatearFechaHoraInput, obtenerFechaHoraActual }
 * @returns {Object} Campos a asignar sobre el ticket activo
 */
export function mapearCotizacionATicket(cotizacionCompleta, { formatearFechaHoraInput, obtenerFechaHoraActual }) {
  const productos = (cotizacionCompleta.detalles_cotizacion || []).map(det => ({
    id_producto: det.id_producto,
    descripcion: det.descripcion_producto,
    precio: parseFloat(det.precio) || 0,
    precioOriginal: parseFloat(det.precio) || 0,
    precioBase: parseFloat(det.precio) || 0,
    precioLista: null,
    fuentePrecio: 'base',
    precioEditado: false,
    cantidad: parseFloat(det.cantidad) || 1,
    indicador_facturacion: det.indicador_facturacion || 1,
    itbis_tasa: parseFloat(det.itbis_tasa) || 0,
    id_tipo_producto: det.id_tipo_producto || det.indicador_bien_servicio || 1
  }));

  return {
    productos,
    productosOriginales: productos.map(p => ({ ...p })),
    clienteSeleccionado: cotizacionCompleta.id_entidad_cliente || null,
    id_entidad_cliente_original: cotizacionCompleta.id_entidad_cliente || null,
    id_lista_precio: cotizacionCompleta.id_lista_precio || null,
    nombreCliente: cotizacionCompleta.nombre_cliente || '',
    comentario: cotizacionCompleta.comentario || '',
    fecha_hora_creacion: formatearFechaHoraInput(cotizacionCompleta.fecha_hora_creacion || obtenerFechaHoraActual()),
    id_cotizacion_origen: cotizacionCompleta.id_cotizacion
  };
}

/**
 * Construye el payload para crear una factura
 * @param {Object} datos - Objeto con todos los datos necesarios
 * @returns {Object} Payload listo para POST /factura/
 */
export function construirPayloadFactura({
  ticket,
  usuario,
  sucursalSeleccionada,
  datosPago,
  metodosPago,
  obtenerFechaHoraActual
}) {
  // Construir detalles de factura
  const detallesFactura = ticket.productos.map(p => ({
    id_producto: p.id_producto,
    descripcion_producto: p.descripcion,
    cantidad: p.cantidad,
    precio: p.precio,
    descuento: 0,
    indicador_facturacion: p.indicador_facturacion,
    itbis_tasa: p.itbis_tasa,
    indicador_bien_servicio: p.id_tipo_producto || 1,
    fuente_precio: p.fuentePrecio || 'base'
  }));
  
  // Determinar condición de pago
  const condicionPago = datosPago.tipoPago;
  
  // Determinar forma de pago DGII
  let dgii_forma_pago;
  if (condicionPago === 'credito') {
    dgii_forma_pago = '04'; // Venta a crédito
  } else if (datosPago.metodosPago.length > 1) {
    dgii_forma_pago = '08'; // Múltiples métodos = Otras formas
  } else {
    // Mapear código UI a código DGII real (ej: UI '04' Cheque → DGII '02')
    const codigoUI = datosPago.metodosPago[0].metodoPago;
    dgii_forma_pago = MAPEO_UI_A_DGII_FORMA_PAGO[codigoUI] || codigoUI;
  }
  
  // Construir pagos programados
  const incluirPagos = condicionPago === 'contado' || datosPago.abonoInicial !== null;
  const fechaHoy = obtenerFechaHoraActual().split('T')[0];
  
  const pagosProgramados = incluirPagos ? datosPago.metodosPago.map(p => ({
    monto: roundTo2(parseFloat(p.monto) || 0),
    monto_recibido: roundTo2(parseFloat(p.monto) || 0),
    id_metodo_pago: obtenerIdMetodoPago(p.metodoPago),
    fecha_pago: fechaHoy,
    referencia: p.detalles?.referencia || p.detalles?.autorizacion || null,
    id_cuenta_tesoreria: p.detalles?.cuentaDestino || null,
    id_pasarela_pago: p.detalles?.id_pasarela_pago || null,
    ultimos_4_digitos: p.detalles?.ultimos4Digitos || null,
    id_banco_origen: p.detalles?.bancoOrigen ? parseInt(p.detalles.bancoOrigen) : null,
    numero_cheque: p.detalles?.numeroCheque || null,
    nombre_titular_cheque: p.detalles?.nombreTitularCheque || null,
    fecha_cobro_cheque: p.detalles?.fechaCobroCheque || null
  })) : [];
  
  // Calcular fecha de vencimiento
  let fechaVencimiento = fechaHoy;
  if (condicionPago === 'credito') {
    // 30 días por defecto para crédito
    const fecha = new Date();
    fecha.setDate(fecha.getDate() + 30);
    fechaVencimiento = fecha.toISOString().split('T')[0];
  }
  
  // Si tipo_comprobante es null/undefined, enviar null (Factura Demo sin NCF)
  const tipoComprobante = ticket.tipoComprobanteFiscal || null;
  const fechaHoraCreacion = ticket.fecha_hora_creacion || obtenerFechaHoraActual();
  const comentarioNormalizado = typeof ticket.comentario === 'string'
    ? ticket.comentario.trim()
    : '';

  return {
    id_entidad_cliente: ticket.clienteSeleccionado ?? ticket.id_entidad_cliente_original ?? null,
    id_entidad_usuario: usuario.id_entidad,
    id_sucursal: sucursalSeleccionada,
    id_tipo_fiscal: 1,
    id_lista_precio: ticket.id_lista_precio ?? null,
    tipo_comprobante: tipoComprobante,
    dgii_id_tipo_ingreso: tipoComprobante ? "01" : null,
    dgii_id_tipo_pago: condicionPago === 'contado' ? 1 : 2,
    dgii_id_forma_pago: tipoComprobante ? dgii_forma_pago : null,
    monto: roundTo2(ticket.totales.subtotal),
    descuento_total: 0,
    impuesto_total: roundTo2(ticket.totales.itbis),
    monto_total: roundTo2(ticket.totales.total),
    fecha_hora_creacion: tipoComprobante ? null : fechaHoraCreacion,
    fecha_emision: ticket.fecha_emision || null,
    fecha_vencimiento: fechaVencimiento,
    comentario: comentarioNormalizado || null,
    id_centro_costo: ticket.id_centro_costo || null,
    detallesFactura,
    incluirPagos,
    pagosProgramados
  };
}

/**
 * Procesa la respuesta exitosa de creación de factura
 * @param {Object} respuesta - Respuesta del backend
 * @returns {Object} Datos procesados
 */
export function procesarRespuestaFactura(respuesta) {
  const datos = respuesta.data?.data || {};
  
  return {
    id_factura: datos.id_factura,
    ncf: datos.ncf,
    balance: datos.balance,
    monto_total: datos.monto_total,
    cambio: datos.cambio || 0,
    monto_recibido: datos.monto_recibido || 0,
    dgii_estado: datos.dgii_estado,
    es_electronica: datos.es_electronica,
    pdf_base64: datos.pdf?.pdf_base64 || null,
    pendienteEnvio: respuesta.data?.pendienteEnvio === true,
    mensaje: respuesta.data?.mensaje
  };
}

/**
 * Determina el mensaje de éxito según el tipo de respuesta
 * @param {Object} resultado - Resultado procesado de la factura
 * @returns {Object} { tipo: 'success'|'warning'|'info', mensaje: string }
 */
export function obtenerMensajeExito(resultado) {
  if (resultado.pendienteEnvio) {
    return {
      tipo: 'info',
      mensaje: `Factura electrónica creada con NCF: ${resultado.ncf}. Envío a DGII pendiente.`
    };
  }
  
  if (resultado.mensaje?.includes('Serie B')) {
    return {
      tipo: 'warning',
      mensaje: `⚠️ ${resultado.mensaje}\n\nNCF físico: ${resultado.ncf}`
    };
  }
  
  if (!resultado.ncf) {
    return {
      tipo: 'success',
      mensaje: `Factura creada exitosamente (sin comprobante fiscal)`
    };
  }

  return {
    tipo: 'success',
    mensaje: `Factura creada exitosamente con NCF: ${resultado.ncf}`
  };
}

